import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CountryFlag from '../atoms/CountryFlag'
import BuildingOfficeSvg from '../other/svg/BuildingOfficeSvg'
import ClipboardDocumentListSvg from '../other/svg/ClipboardDocumentListSvg'
import CopySvg from '../other/svg/CopySvg'
import ToastSuccessSvg from '../other/svg/ToastSuccessSvg'
import MoreHorizontalSvg from '../other/svg/MoreHorizontalSvg'
import PencilSvg from '../other/svg/PencilSvg'
import TrashCanSvg from '../other/svg/TrashCanSvg'
import UserCircleSvg from '../other/svg/UserCircleSvg'

interface ClientTableRowProps {
  name: string
  email: string
  phoneNumber: string
  country: string | null
  clientType: 'personal' | 'company' | null
  isMenuOpen: boolean
  onToggleMenu: () => void
  onCloseMenu: () => void
  onOpenDetails: () => void
  onEdit: () => void
  onDelete: () => void
}

interface ClientMenuItemProps {
  icon: JSX.Element
  label: string
  onClick: () => void
}

const menuShadow =
  '0px 8px 16px 0px rgba(0,0,0,0.08), 0px 4px 8px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'

const copyButtonShadow = '0px 0px 0px 1px rgba(0,0,0,0.08), 0px 1px 2px 0px rgba(0,0,0,0.12)'

const COUNTRY_CODES: Record<string, string> = {
  poland: 'PL',
  germany: 'DE'
}

const displayValue = (value: string): string => {
  return value.trim().length === 0 ? '-' : value
}

const ClientMenuItem = ({ icon, label, onClick }: ClientMenuItemProps): JSX.Element => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="self-stretch px-2 py-1 bg-backgrounds-bg-field-component rounded-sm inline-flex justify-start items-center gap-2 hover:bg-lightGrey">
      <div className="w-4 h-4 relative overflow-hidden text-buttons-text-secondary flex justify-center items-center">
        {icon}
      </div>
      <div className="flex-1 justify-start text-left text-foregrounds-fg-base text-xs font-medium font-Montserrat leading-5">
        {label}
      </div>
    </button>
  )
}

const ClientTableRow = ({
  name,
  email,
  phoneNumber,
  country,
  clientType,
  isMenuOpen,
  onToggleMenu,
  onCloseMenu,
  onOpenDetails,
  onEdit,
  onDelete
}: ClientTableRowProps): JSX.Element => {
  const { t } = useTranslation()
  const menuRef = useRef<HTMLDivElement>(null)
  const copiedTimeout = useRef<number | null>(null)
  const [copied, setCopied] = useState(false)
  const countryCode = country === null ? null : COUNTRY_CODES[country] ?? null
  const countryLabel =
    country === 'poland' || country === 'germany' ? t(`clients.countries.${country}`) : displayValue(country ?? '')

  useEffect(() => {
    const handleClick = (event: MouseEvent): void => {
      if (menuRef.current !== null && !menuRef.current.contains(event.target as Node)) {
        onCloseMenu()
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClick)
    }

    return () => {
      document.removeEventListener('mousedown', handleClick)
    }
  }, [isMenuOpen, onCloseMenu])

  useEffect(() => {
    return () => {
      if (copiedTimeout.current !== null) {
        window.clearTimeout(copiedTimeout.current)
      }
    }
  }, [])

  const handleCopyEmail = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation()
    if (email.trim().length === 0) {
      return
    }
    void navigator.clipboard.writeText(email).then(() => {
      setCopied(true)
      if (copiedTimeout.current !== null) {
        window.clearTimeout(copiedTimeout.current)
      }
      copiedTimeout.current = window.setTimeout(() => {
        setCopied(false)
        copiedTimeout.current = null
      }, 1500)
    })
  }

  return (
    <>
      <div className="self-stretch h-px bg-borders-border-base" />
      <div
        role="button"
        tabIndex={0}
        onClick={onOpenDetails}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onOpenDetails()
          }
        }}
        className={`self-stretch h-16 px-6 inline-flex justify-center items-center gap-3 cursor-pointer ${
          isMenuOpen
            ? 'bg-backgrounds-bg-highlight'
            : 'bg-backgrounds-bg-field-component hover:bg-backgrounds-bg-field-component-hover'
        }`}>
        <div className="flex-1 self-stretch flex justify-start items-center gap-3 min-w-0">
          <div className="flex-1 self-stretch flex justify-start items-center gap-1.5 min-w-0">
            <div className="text-icon-secondary">
              {clientType === 'personal' ? (
                <UserCircleSvg className="w-6 h-6" />
              ) : (
                <BuildingOfficeSvg className="w-6 h-6" />
              )}
            </div>
            <div className="flex-1 justify-start text-foregrounds-fg-base text-xs font-semibold font-Montserrat leading-5 truncate">
              {displayValue(name)}
            </div>
          </div>
          <div className="flex-1 self-stretch pr-8 flex justify-between items-center min-w-0 gap-2">
            <div className="flex-1 justify-start text-foregrounds-fg-base text-xs font-semibold font-Montserrat leading-5 truncate">
              {displayValue(email)}
            </div>
            {email.trim().length > 0 && (
              <button
                type="button"
                aria-label={copied ? t('clients.email_copied') : t('clients.copy_email')}
                onClick={handleCopyEmail}
                className="p-2 bg-backgrounds-bg-component rounded-md flex justify-center items-center overflow-hidden text-icon-secondary"
                style={{ boxShadow: copyButtonShadow }}>
                {copied ? (
                  <ToastSuccessSvg className="w-3.5 h-3.5 text-tag-success-tag-green-icon" />
                ) : (
                  <CopySvg className="w-3.5 h-3.5" />
                )}
              </button>
            )}
          </div>
          <div className="w-48 self-stretch pr-8 flex justify-start items-center overflow-hidden">
            <div className="flex-1 justify-start text-foregrounds-fg-base text-xs font-semibold font-Montserrat leading-5 truncate">
              {displayValue(phoneNumber)}
            </div>
          </div>
          <div className="w-40 self-stretch flex justify-start items-center gap-1.5 min-w-0">
            {countryCode !== null && <CountryFlag countryCode={countryCode} width={20} height={14} />}
            <div className="justify-start text-foregrounds-fg-base text-xs font-semibold font-Montserrat leading-5 truncate">
              {countryLabel}
            </div>
          </div>
          <div
            className="self-stretch px-px flex justify-end items-center overflow-visible"
            onClick={(event) => {
              event.stopPropagation()
            }}>
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                aria-label={t('clients.actions')}
                onClick={onToggleMenu}
                className={`p-2.5 rounded-md flex justify-center items-center overflow-hidden ${
                  isMenuOpen ? 'bg-zinc-900/10' : 'bg-transparent hover:bg-lightGrey'
                }`}>
                <MoreHorizontalSvg className="w-3.5 h-3.5 text-icon-secondary" />
              </button>
              {isMenuOpen && (
                <div
                  className="w-44 p-1 absolute right-0 top-full z-20 bg-backgrounds-bg-field-component rounded-lg inline-flex flex-col justify-start items-start overflow-hidden"
                  style={{ boxShadow: menuShadow }}>
                  <ClientMenuItem
                    icon={<ClipboardDocumentListSvg className="w-4 h-4" />}
                    label={t('clients.details')}
                    onClick={() => {
                      onCloseMenu()
                      onOpenDetails()
                    }}
                  />
                  <div className="self-stretch h-2 relative -mx-1">
                    <div className="absolute left-0 right-0 top-[3px] h-px bg-borders-border-menu-top" />
                    <div className="absolute left-0 right-0 top-[4px] h-px bg-borders-border-menu-bot" />
                  </div>
                  <ClientMenuItem
                    icon={<PencilSvg className="w-4 h-4" />}
                    label={t('clients.edit')}
                    onClick={() => {
                      onCloseMenu()
                      onEdit()
                    }}
                  />
                  <ClientMenuItem
                    icon={<TrashCanSvg className="w-4 h-4" />}
                    label={t('clients.delete')}
                    onClick={() => {
                      onCloseMenu()
                      onDelete()
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ClientTableRow
